import PropTypes from "prop-types";
import { useState } from "react";

const inputClass =
  "w-full mt-1 rounded-[2px] px-3 py-2.5 bg-white/10 border border-white/30 focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white placeholder-white/30 text-sm md:text-base font-fira-sans";

const labelClass = "block text-sm font-medium text-white/50";

export default function PartnershipForm({ onClose }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    whatsapp: '',
    platforms: [],
    links: [''],
    niches: [],
    audienceSize: '',
    country: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Format timestamp including seconds
  const getFormattedTimestamp = () =>
    new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    });

  const addLink = () => {
    if (formData.links.length < 5) {
      setFormData(prev => ({
        ...prev,
        links: [...prev.links, '']
      }));
    }
  };
  
  const updateLink = (index, value) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.map((link, i) => i === index ? value : link)
    }));
  };
  
  const removeLink = (index) => {
    if (formData.links.length > 1) {
      setFormData(prev => ({
        ...prev,
        links: prev.links.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  // Basic validation
  const validateForm = () => {
    if (!formData.fullName.trim()) return "Full Name is required.";
    if (!formData.email.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return "Please enter a valid email address.";
    if (formData.whatsapp && !/^\d+$/.test(formData.whatsapp)) return "WhatsApp number must contain only digits.";
    if (formData.platforms.length === 0) return "Please select at least one platform.";
    if (formData.links.filter(l => l.trim() !== "").length === 0) return "Please provide at least one valid link.";
    if (formData.niches.length === 0) return "Please select at least one niche.";
    if (!formData.audienceSize.trim()) return "Audience size is required.";
    if (isNaN(formData.audienceSize)) return "Audience size must be a number.";
    if (!formData.country.trim()) return "Country is required.";
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      setIsSubmitting(false);
      return;
    }

    const scriptUrl = "https://script.google.com/macros/s/AKfycbwn4RWrQr2SOFNwByQzzmnLw8uKX8hud_d1ja6kU6_uJKUXso0JWPLURAanx1E4T7rLmQ/exec";

    try {
      const form = document.createElement('form');
      const iframeId = `partnership_iframe_${Date.now()}`;
      form.method = 'POST';
      form.action = scriptUrl;
      form.target = iframeId;
      form.style.display = 'none';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'data';
      input.value = JSON.stringify({
        ...formData,
        platforms: formData.platforms.join(', '),
        niches: formData.niches.join(', '),
        links: formData.links.filter(link => link.trim() !== '').join(', '),
        timestamp: getFormattedTimestamp(),
      });
      form.appendChild(input);

      const iframe = document.createElement('iframe');
      iframe.name = iframeId;
      iframe.id = iframeId;
      iframe.style.display = 'none';

      document.body.appendChild(iframe);
      document.body.appendChild(form);

      const cleanup = () => {
        document.body.removeChild(form);
        document.body.removeChild(iframe);
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        setSuccessMessage("Your partnership application has been submitted successfully!");
        setIsSubmitting(false);
        setTimeout(() => {
          onClose();
        }, 2000);
      }, 10000);

      iframe.onload = () => {
        clearTimeout(timeoutId);
        cleanup();
        setSuccessMessage("Your partnership application has been submitted successfully!");
        setIsSubmitting(false);
        setTimeout(() => {
          onClose();
        }, 2000);
      };

      form.submit();
    } catch (err) {
      console.error(err);
      setErrorMessage("There was an error submitting your application. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center font-fira-sans p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        className="relative z-[100] flex w-full max-w-2xl max-h-[95vh] flex-col overflow-hidden"
        style={{
          backgroundColor: "#141516",
          border: "1px solid rgba(255, 255, 255, 0.16)",
          borderRadius: "2px",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 flex h-8 w-8 items-center justify-center rounded-[2px] bg-white/10 transition-colors hover:bg-white/20 md:top-5 md:right-5 md:h-10 md:w-10"
          aria-label="Close"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="flex-shrink-0 border-b border-white/10 px-5 pb-5 pt-5 pr-14 md:px-8 md:pb-6 md:pt-8 md:pr-16">
          <h2 className="text-xl font-normal text-white md:text-2xl">
            Apply for Partnership
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-white/50 md:text-base">
            Fill out the form below to apply for our creator partnership program.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-8 md:py-6">
          <div className="space-y-5 md:space-y-6">
            <div>
              <label htmlFor="partnership-fullName" className={labelClass}>
                Full Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="partnership-fullName"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({...prev, fullName: e.target.value}))}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label htmlFor="partnership-email" className={labelClass}>
                Email Address <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="partnership-email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label htmlFor="partnership-whatsapp" className={labelClass}>
                WhatsApp Number
              </label>
              <input
                type="text"
                id="partnership-whatsapp"
                placeholder="Enter your WhatsApp number (optional)"
                value={formData.whatsapp}
                onChange={(e) => setFormData(prev => ({...prev, whatsapp: e.target.value}))}
                className={inputClass}
              />
            </div>

            <div>
              <p className={`${labelClass} mb-3`}>
                Platform(s) You Create On <span className="text-red-400">*</span>
              </p>
              <div className="grid grid-cols-1 gap-2.5 text-sm sm:grid-cols-2 sm:gap-3">
                {["YouTube", "Instagram", "LinkedIn", "TikTok", "Twitter/X", "Newsletter", "Other"].map(
                  (platform) => (
                    <label key={platform} className="flex cursor-pointer items-center gap-2.5 text-white/80">
                      <input 
                        type="checkbox" 
                        className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/10 text-[#B8EF43] accent-[#B8EF43] focus:ring-2 focus:ring-[#B8EF43] focus:ring-offset-0 focus:ring-offset-[#141516]"
                        checked={formData.platforms.includes(platform)}
                        onChange={() => handleCheckboxChange('platforms', platform)}
                      />
                      <span>{platform}</span>
                    </label>
                  )
                )}
              </div>
            </div>

            <div>
              <label htmlFor="partnership-link-0" className={labelClass}>
                Link(s) to Your Channel / Profile / Newsletter{" "}
                <span className="text-red-400">*</span>
              </label>
              <div className="space-y-3">
                {formData.links.map((link, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="url"
                      id={`partnership-link-${index}`}
                      placeholder="https://example.com/your-profile"
                      value={link}
                      onChange={(e) => updateLink(index, e.target.value)}
                      className={inputClass}
                      required={index === 0}
                    />
                    {formData.links.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLink(index)}
                        className="shrink-0 rounded-[2px] px-3 text-lg text-red-400 transition-colors hover:bg-white/10 hover:text-red-300"
                        aria-label="Remove link"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {formData.links.length < 5 && (
                <button
                  type="button"
                  onClick={addLink}
                  className="mt-2 text-sm font-medium text-[#B8EF43] transition-colors hover:text-[#c8f563]"
                >
                  + Add Another Link
                </button>
              )}
            </div>

            <div>
              <p className={`${labelClass} mb-3`}>
                Audience Niche <span className="text-red-400">*</span>
              </p>
              <div className="grid grid-cols-1 gap-2.5 text-sm sm:grid-cols-2 sm:gap-3">
                {["AI", "Finance", "Lifestyle", "Business", "Productivity", "Other"].map(
                  (niche) => (
                    <label key={niche} className="flex cursor-pointer items-center gap-2.5 text-white/80">
                      <input 
                        type="checkbox" 
                        className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/10 accent-[#B8EF43] focus:ring-2 focus:ring-[#B8EF43] focus:ring-offset-0 focus:ring-offset-[#141516]"
                        checked={formData.niches.includes(niche)}
                        onChange={() => handleCheckboxChange('niches', niche)}
                      />
                      <span>{niche}</span>
                    </label>
                  )
                )}
              </div>
            </div>

            <div>
              <label htmlFor="partnership-audienceSize" className={labelClass}>
                Audience Size (approx.) <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="partnership-audienceSize"
                placeholder="e.g., 10000"
                value={formData.audienceSize}
                onChange={(e) => setFormData(prev => ({...prev, audienceSize: e.target.value}))}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label htmlFor="partnership-country" className={labelClass}>
                Country You Are Based In <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="partnership-country"
                placeholder="Enter your country"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({...prev, country: e.target.value}))}
                className={inputClass}
                required
              />
            </div>

            {(successMessage || errorMessage) && (
              <div className="space-y-3 pt-1">
                {successMessage && (
                  <div className="rounded-[2px] border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                    {successMessage}
                  </div>
                )}
                {errorMessage && (
                  <div className="rounded-[2px] border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {errorMessage}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-white/10 bg-black/20 px-5 py-4 md:px-8 md:py-5">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-[2px] border border-white/30 py-2.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 sm:w-auto sm:min-w-[120px] sm:px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full rounded-[2px] bg-[#B8EF43] py-2.5 text-sm font-medium text-black transition hover:bg-[#a8d93a] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[180px] sm:px-6"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

PartnershipForm.propTypes = {
  onClose: PropTypes.func.isRequired,
};
